import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SignInOutput } from 'aws-amplify/auth';
import { signIn as authSignIn, signOut as authSignOut, AuthUser, getCurrentUser } from '../services/auth';

interface AuthContextType {
    user: AuthUser | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<SignInOutput | void>;
    signOut: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        if (import.meta.env.VITE_MOCK_AUTH === 'true') {
            setUser({ email: 'dev@local', sub: 'mock-user-id' });
            setLoading(false);
            return;
        }

        try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const signIn = useCallback(async (email: string, password: string): Promise<SignInOutput | void> => {
        const result = await authSignIn(email, password);
        if (result && result.isSignedIn) {
            await checkAuth();
        }
        return result;
    }, [checkAuth]);

    const signOut = useCallback(async () => {
        await authSignOut();
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        await checkAuth();
    }, [checkAuth]);

    const value = useMemo(() => ({
        user,
        loading,
        signIn,
        signOut,
        refreshUser,
    }), [user, loading, signIn, signOut, refreshUser]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
